import { useEffect, useRef, useState } from "react";

/* Keeps an overlay mounted long enough to play its close transition.
   Wrap an existing `{open && (...)}` block:  <Presence show={open} exitVar="--modal-close-dur">{open && (...)}</Presence>
   While closing it re-renders the last open content with data-state="closing";
   styles/motion.css animates that state. Reduced motion skips the wait. */
const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const readMs = (cssVar, fallback) => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  const ms = raw.endsWith("ms") ? parseFloat(raw) : raw.endsWith("s") ? parseFloat(raw) * 1000 : NaN;
  return Number.isFinite(ms) ? ms : fallback;
};

export default function Presence({ show, exitVar = "--modal-close-dur", children }) {
  const [mounted, setMounted] = useState(!!show);
  const last = useRef(children);
  if (show) last.current = children;

  useEffect(() => {
    if (show) { setMounted(true); return; }
    if (!mounted) return;
    if (prefersReducedMotion()) { setMounted(false); return; }
    const t = setTimeout(() => setMounted(false), readMs(exitVar, 150));
    return () => clearTimeout(t);
  }, [show, mounted, exitVar]);

  if (!show && !mounted) return null;
  return (
    <div className="vs-presence" data-state={show ? "open" : "closing"}>
      {show ? children : last.current}
    </div>
  );
}
